import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  Mail,
  Calendar,
  Trash2,
  Download,
  Search,
  Users,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'

interface Subscriber {
  id: string
  email: string
  source: string
  subscribed_at: string
  is_active: boolean
}

export function SubscribersPage() {
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()

  const { data: subscribers, isLoading } = useQuery({
    queryKey: ['admin-subscribers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .select('*')
        .order('subscribed_at', { ascending: false })
      if (error) throw error
      return data as Subscriber[]
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .update({ is_active })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscribers'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .delete()
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscribers'] })
    },
  })

  const filteredSubscribers = subscribers?.filter((sub) =>
    sub.email.toLowerCase().includes(search.toLowerCase())
  )

  const activeCount = subscribers?.filter((s) => s.is_active).length || 0
  const totalCount = subscribers?.length || 0

  const exportToCSV = () => {
    if (!subscribers || subscribers.length === 0) return

    const headers = ['Email', 'Source', 'Subscribed At', 'Active']
    const rows = subscribers.map((s) => [
      s.email,
      s.source,
      new Date(s.subscribed_at).toLocaleString(),
      s.is_active ? 'Yes' : 'No',
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  const getRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return formatDate(dateStr)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Newsletter Subscribers</h1>
          <p className="text-secondary-600">Manage email subscriptions from Coming Soon page</p>
        </div>
        <button
          onClick={exportToCSV}
          disabled={!subscribers || subscribers.length === 0}
          className="btn-outline"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-secondary-900">{totalCount}</p>
            <p className="text-sm text-secondary-500">Total Subscribers</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-secondary-900">{activeCount}</p>
            <p className="text-sm text-secondary-500">Active</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center">
            <XCircle className="w-6 h-6 text-secondary-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-secondary-900">{totalCount - activeCount}</p>
            <p className="text-sm text-secondary-500">Unsubscribed</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
          <input
            type="text"
            placeholder="Search by email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Subscribers List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : filteredSubscribers && filteredSubscribers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50 border-b border-secondary-200">
                <tr>
                  <th className="text-left text-xs font-medium text-secondary-500 uppercase px-6 py-3">
                    Email
                  </th>
                  <th className="text-left text-xs font-medium text-secondary-500 uppercase px-6 py-3">
                    Source
                  </th>
                  <th className="text-left text-xs font-medium text-secondary-500 uppercase px-6 py-3">
                    Subscribed
                  </th>
                  <th className="text-left text-xs font-medium text-secondary-500 uppercase px-6 py-3">
                    Status
                  </th>
                  <th className="text-right text-xs font-medium text-secondary-500 uppercase px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {filteredSubscribers.map((subscriber) => (
                  <motion.tr
                    key={subscriber.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-secondary-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-primary-600" />
                        </div>
                        <span className="font-medium text-secondary-900">
                          {subscriber.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="badge bg-secondary-100 text-secondary-600">
                        {subscriber.source}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-secondary-600">
                        <Calendar className="w-4 h-4" />
                        <span title={formatDate(subscriber.subscribed_at)}>
                          {getRelativeTime(subscriber.subscribed_at)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() =>
                          toggleActiveMutation.mutate({
                            id: subscriber.id,
                            is_active: !subscriber.is_active,
                          })
                        }
                        className={cn(
                          'badge flex items-center gap-1',
                          subscriber.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-secondary-100 text-secondary-600'
                        )}
                      >
                        {subscriber.is_active ? (
                          <>
                            <CheckCircle className="w-3 h-3" /> Active
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3" /> Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => {
                            if (confirm('Delete this subscriber?')) {
                              deleteMutation.mutate(subscriber.id)
                            }
                          }}
                          className="p-2 text-secondary-400 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <Mail className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500">No subscribers yet</p>
            <p className="text-sm text-secondary-400 mt-1">
              Subscribers will appear here when people sign up on the Coming Soon page
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
